import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Building2, Users, GraduationCap, Briefcase, Search, Download,
  MoreVertical, Star, StarOff, Trash2, Edit, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, ArrowUpDown, Eye, FileText, UserCheck, UserX,
  BarChart3, PieChart, BookOpen, Bell, CreditCard, LogIn, Plus,
  Upload, Megaphone, Globe, Layers, ShieldAlert, Clock, Filter, Mail, Sparkles,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart as RPieChart, Pie, Cell, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import CMSTab from '@/components/admin/CMSTab';
import TenantManagementTab from '@/components/admin/TenantManagementTab';
import EmailTemplatesTab from '@/components/admin/EmailTemplatesTab';
import LoginActivityCard from '@/components/admin/LoginActivityCard';

const TABS = [
  { id: 'overview', label: 'Dashboard', icon: TrendingUp },
  { id: 'tenants', label: 'Tenants', icon: Layers },
  { id: 'organizations', label: 'Institutions', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'candidates', label: 'Candidates', icon: GraduationCap },
  { id: 'jobs', label: 'Jobs', icon: Briefcase },
  { id: 'blogs', label: 'Blogs', icon: BookOpen },
  { id: 'job-alerts', label: 'Job Alerts', icon: Bell },
  { id: 'transactions', label: 'Transactions', icon: CreditCard },
  { id: 'imports', label: 'Imports', icon: Upload },
  { id: 'cms', label: 'CMS', icon: Globe },
  { id: 'emails', label: 'Emails', icon: Mail },
  { id: 'audit-logs', label: 'Audit Logs', icon: ShieldAlert },
  { id: 'client-analytics', label: 'Analytics', icon: BarChart3 },
];

const AdminPage = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (user?.role !== 'super_admin') {
    return (
      <DashboardLayout>
        <div className="text-center py-20 text-slate-500">Access denied. Super Admin only.</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex min-h-[calc(100vh-80px)] -mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6" data-testid="admin-page">
        {/* Sidebar */}
        <aside className={`bg-slate-900 text-white flex-shrink-0 transition-all duration-200 ${sidebarCollapsed ? 'w-16' : 'w-56'}`}>
          <div className="sticky top-0 h-[calc(100vh-80px)] flex flex-col">
            {/* Sidebar Header */}
            <div className="flex items-center justify-between px-3 py-4 border-b border-slate-700/50">
              {!sidebarCollapsed && (
                <span className="text-sm font-semibold text-slate-300 tracking-wide uppercase">Admin</span>
              )}
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
                data-testid="sidebar-toggle"
              >
                {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
            </div>

            {/* Nav Items */}
            <nav className="flex-1 py-2 overflow-y-auto">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    data-testid={`admin-tab-${tab.id}`}
                    title={sidebarCollapsed ? tab.label : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  >
                    <tab.icon className="w-4 h-4 flex-shrink-0" />
                    {!sidebarCollapsed && <span className="truncate">{tab.label}</span>}
                  </button>
                );
              })}
            </nav>

            {/* Visit Site */}
            <div className="p-3 border-t border-slate-700/50">
              <a href="/" target="_blank" rel="noreferrer" className="block">
                <button className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-slate-400 hover:text-white hover:bg-slate-800 transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}>
                  <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>Visit Website</span>}
                </button>
              </a>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                {TABS.find(t => t.id === activeTab)?.label || 'Admin'}
              </h1>
            </div>

            {activeTab === 'overview' && <OverviewTab token={token} />}
            {activeTab === 'tenants' && <TenantManagementTab token={token} />}
            {activeTab === 'organizations' && <OrganizationsTab token={token} />}
            {activeTab === 'users' && <UsersTab token={token} />}
            {activeTab === 'candidates' && <CandidatesTab token={token} />}
            {activeTab === 'jobs' && <JobsTab token={token} />}
            {activeTab === 'blogs' && <BlogsTab token={token} />}
            {activeTab === 'job-alerts' && <JobAlertsTab token={token} />}
            {activeTab === 'transactions' && <TransactionsTab token={token} />}
            {activeTab === 'imports' && <ImportsTab token={token} />}
            {activeTab === 'cms' && <CMSTab token={token} />}
            {activeTab === 'emails' && <EmailTemplatesTab token={token} />}
            {activeTab === 'audit-logs' && <AuditLogsTab token={token} />}
            {activeTab === 'client-analytics' && <ClientAnalyticsTab token={token} />}
          </div>
        </main>
      </div>
    </DashboardLayout>
  );
};

/* ==================== OVERVIEW TAB ==================== */
const OverviewTab = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [candAnalytics, setCandAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendGranularity, setTrendGranularity] = useState('day');
  const [trendPeriods, setTrendPeriods] = useState(30);
  const [trendData, setTrendData] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`${API}/admin/dashboard`, { headers, withCredentials: true }),
      axios.get(`${API}/admin/analytics/candidates`, { headers, withCredentials: true }),
    ])
      .then(([dashRes, candRes]) => {
        setStats(dashRes.data);
        setCandAnalytics(candRes.data);
      })
      .catch(() => toast.error('Failed to load admin stats'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    setTrendLoading(true);
    axios.get(`${API}/admin/analytics/trends`, {
      headers, withCredentials: true,
      params: { granularity: trendGranularity, periods: trendPeriods },
    })
      .then(res => setTrendData(res.data.data || []))
      .catch(() => setTrendData([]))
      .finally(() => setTrendLoading(false));
  }, [token, trendGranularity, trendPeriods]);

  if (loading) return <LoadingState />;

  const s = stats?.stats || {};
  const ps = s.profile_stats || {};

  const statCards = [
    { label: 'Total Candidates', value: s.total_candidates, icon: GraduationCap, color: 'bg-blue-50 text-blue-600', border: 'border-blue-100', sub: `+${s.candidates_this_week || 0} this week` },
    { label: 'Applications', value: s.total_applications, icon: FileText, color: 'bg-amber-50 text-amber-600', border: 'border-amber-100', sub: `+${s.applications_this_month || 0} this month` },
    { label: 'Active Jobs', value: s.active_jobs, icon: Briefcase, color: 'bg-green-50 text-green-600', border: 'border-green-100', sub: `${s.total_jobs || 0} total` },
    { label: 'Institutions', value: s.total_employers, icon: Building2, color: 'bg-indigo-50 text-indigo-600', border: 'border-indigo-100', sub: `${s.paid_employers || 0} paid` },
  ];

  const miniStats = [
    { label: 'Today\'s Signups', value: s.candidates_today || 0 },
    { label: 'This Month', value: s.candidates_this_month || 0 },
    { label: 'With Phone', value: ps.with_phone || 0 },
    { label: 'With Resume', value: ps.with_resume || 0 },
    { label: 'Video Resumes', value: ps.with_video || 0 },
    { label: 'Published Blogs', value: s.total_blogs || 0 },
  ];

  const candPieData = candAnalytics ? [
    { name: 'Applied', value: candAnalytics.applied, fill: '#3B82F6' },
    { name: 'Not Applied', value: candAnalytics.not_applied, fill: '#E2E8F0' },
  ] : [];

  const statusData = candAnalytics?.status_breakdown
    ? Object.entries(candAnalytics.status_breakdown).map(([key, val]) => ({ name: key, value: val }))
    : [];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((c) => (
          <Card key={c.label} className={`border ${c.border}`} data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}>
                  <c.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                {c.value ?? 0}
              </p>
              <p className="text-sm text-slate-500 mt-1">{c.label}</p>
              {c.sub && <p className="text-xs text-slate-400 mt-0.5">{c.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Stats Bar */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {miniStats.map((ms) => (
              <div key={ms.label} className="text-center">
                <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{ms.value}</p>
                <p className="text-xs text-slate-500">{ms.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Trend Charts with Y/M/D toggle */}
      <div className="space-y-3" data-testid="trends-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Trends</h2>
            <p className="text-xs text-slate-500">Real signup and application activity over time</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="granularity-toggle">
              {['day', 'month', 'year'].map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => {
                    setTrendGranularity(g);
                    setTrendPeriods(g === 'day' ? 30 : g === 'month' ? 12 : 5);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-colors ${trendGranularity === g ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`granularity-${g}`}
                >
                  {g}
                </button>
              ))}
            </div>
            <Select value={String(trendPeriods)} onValueChange={(v) => setTrendPeriods(parseInt(v, 10))}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="trend-periods"><SelectValue /></SelectTrigger>
              <SelectContent>
                {trendGranularity === 'day' && (
                  <>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </>
                )}
                {trendGranularity === 'month' && (
                  <>
                    <SelectItem value="3">Last 3 months</SelectItem>
                    <SelectItem value="6">Last 6 months</SelectItem>
                    <SelectItem value="12">Last 12 months</SelectItem>
                    <SelectItem value="24">Last 24 months</SelectItem>
                  </>
                )}
                {trendGranularity === 'year' && (
                  <>
                    <SelectItem value="3">Last 3 years</SelectItem>
                    <SelectItem value="5">Last 5 years</SelectItem>
                    <SelectItem value="10">Last 10 years</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Card data-testid="candidates-trend-card">
            <CardHeader className="pb-2"><CardTitle style={{ fontFamily: 'Outfit' }}>Candidate Signups</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {trendLoading ? (
                  <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <defs>
                        <linearGradient id="candGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="candidates" name="Candidates" stroke="#3B82F6" fill="url(#candGrad)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="applications-trend-card">
            <CardHeader className="pb-2"><CardTitle style={{ fontFamily: 'Outfit' }}>Applications</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {trendLoading ? (
                  <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" /></div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="applications" name="Applications" fill="#6366F1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Login Activity */}
      <LoginActivityCard token={token} defaultLimit={6} />

      {/* Candidate Analytics + Recent */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Candidate Activity</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <RPieChart>
                  <Pie data={candPieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                    {candPieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </RPieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2 text-sm">
              <div className="text-center">
                <p className="text-lg font-bold text-blue-600">{candAnalytics?.applied || 0}</p>
                <p className="text-slate-500">Applied</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-slate-400">{candAnalytics?.not_applied || 0}</p>
                <p className="text-slate-500">Not Applied</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Application Status</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#6366F1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Recent Signups</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.recent_users?.slice(0, 8).map((u) => (
                <div key={u.user_id} className="flex items-center justify-between py-1">
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{u.name || u.email?.split('@')[0]}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                  <RoleBadge role={u.role} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

/* ==================== CLIENT ANALYTICS TAB ==================== */
const ClientAnalyticsTab = ({ token }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clickData, setClickData] = useState(null);
  const [clickDays, setClickDays] = useState(30);

  const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};

  useEffect(() => {
    axios.get(`${API}/admin/analytics/clients`, { headers, withCredentials: true })
      .then(res => setClients(res.data.clients || []))
      .catch(() => toast.error('Failed to load client analytics'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    axios.get(`${API}/admin/analytics/clicks?days=${clickDays}`, { headers, withCredentials: true })
      .then(res => setClickData(res.data))
      .catch(() => {});
  }, [token, clickDays]);

  if (loading) return <LoadingState />;

  const chartData = clients.map(c => ({
    name: c.name?.length > 15 ? c.name.slice(0, 15) + '...' : c.name,
    jobs: c.total_jobs,
    applications: c.total_applications,
    hired: c.hired,
  }));

  return (
    <div className="space-y-6" data-testid="admin-client-analytics">
      {/* Partner Click Tracking */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ fontFamily: 'Outfit' }}>Partner Link Clicks</CardTitle>
            <Select value={String(clickDays)} onValueChange={(v) => setClickDays(Number(v))}>
              <SelectTrigger className="w-36" data-testid="click-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {clickData?.summary?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {clickData.summary.map((s) => {
                const label = s.link_type === 'wadhwani_upskilling' ? 'Wadhwani Foundation' : s.link_type === 'zety_resume' ? 'Zety Resume Builder' : s.link_type;
                const color = s.link_type === 'wadhwani_upskilling' ? 'indigo' : 'blue';
                return (
                  <div key={s.link_type} className={`p-5 rounded-xl border bg-${color}-50/50 border-${color}-100`} data-testid={`click-stat-${s.link_type}`}>
                    <p className="text-sm font-medium text-slate-600 mb-1">{label}</p>
                    <p className="text-3xl font-bold text-slate-900">{s.total_clicks}</p>
                    <div className="flex gap-4 mt-2 text-xs text-slate-500">
                      <span>{s.unique_users} logged-in users</span>
                      <span>{s.anonymous_clicks} anonymous</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">No click data yet. Clicks on Wadhwani and Zety links will appear here.</p>
          )}

          {clickData?.recent_clicks?.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-slate-700 mb-3">Recent Clicks</p>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 border-b sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Partner</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">User</th>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clickData.recent_clicks.slice(0, 20).map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-xs">
                            {c.link_type === 'wadhwani_upskilling' ? 'Wadhwani' : c.link_type === 'zety_resume' ? 'Zety' : c.link_type}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{c.user_email || 'Anonymous'}</td>
                        <td className="px-3 py-2 text-slate-400">{new Date(c.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Performance by Client</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="jobs" fill="#6366F1" name="Job Posts" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="applications" fill="#3B82F6" name="Applications" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="hired" fill="#10B981" name="Hired" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Client Details</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Institution</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Tier</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Total Jobs</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Active Jobs</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Applications</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Est. Views</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Hired</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Rejected</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.org_id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.institution_type || '—'}</p>
                    </td>
                    <td className="px-4 py-3 text-center"><TierBadge tier={c.subscription_tier} /></td>
                    <td className="px-4 py-3 text-center font-medium">{c.total_jobs}</td>
                    <td className="px-4 py-3 text-center">{c.active_jobs}</td>
                    <td className="px-4 py-3 text-center font-medium text-blue-600">{c.total_applications}</td>
                    <td className="px-4 py-3 text-center text-slate-500">{c.views}</td>
                    <td className="px-4 py-3 text-center text-green-600 font-medium">{c.hired}</td>
                    <td className="px-4 py-3 text-center text-red-500">{c.rejected}</td>
                  </tr>
                ))}
                {!clients.length && (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-400">No clients found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ==================== ORGANIZATIONS TAB ==================== */
const OrganizationsTab = ({ token }) => {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editTierOrg, setEditTierOrg] = useState(null);
  const limit = 10;

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { skip: page * limit, limit, sort_by: sortBy, sort_order: sortOrder };
    if (search) params.search = search;
    try {
      const res = await axios.get(`${API}/admin/organizations`, { headers, params, withCredentials: true });
      setOrgs(res.data.organizations);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load institutions'); }
    setLoading(false);
  }, [token, page, sortBy, sortOrder, search]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const toggleFeatured = async (orgId, current) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.put(`${API}/admin/organizations/${orgId}/feature?is_featured=${!current}`, {}, { headers, withCredentials: true });
      toast.success(current ? 'Unfeatured' : 'Featured');
      fetchOrgs();
    } catch { toast.error('Failed to update'); }
  };

  const deleteOrg = async (orgId) => {
    if (!window.confirm('Delete this institution and all related data?')) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.delete(`${API}/admin/organizations/${orgId}`, { headers, withCredentials: true });
      toast.success('Deleted');
      fetchOrgs();
    } catch { toast.error('Failed to delete'); }
  };

  const updateTier = async (orgId, tier) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.put(`${API}/admin/organizations/${orgId}/tier?tier=${tier}`, {}, { headers, withCredentials: true });
      toast.success(`Tier updated to ${tier}`);
      setEditTierOrg(null);
      fetchOrgs();
    } catch { toast.error('Failed to update tier'); }
  };

  const exportData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/admin/export/organizations`, { headers, withCredentials: true });
      downloadCSV(res.data.data, 'institutions_export');
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search institutions..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="admin-org-search" />
          </div>
          <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
            <SelectTrigger className="w-40" data-testid="admin-org-sort">
              <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Date Created</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={exportData} data-testid="admin-org-export">
          <Download className="w-4 h-4 mr-2" />Export
        </Button>
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Institution</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Type</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Users</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Jobs</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Tier</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Featured</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((org) => (
                    <tr key={org.org_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-slate-900">{org.name}</p>
                          <PartnerBadge org={org} />
                        </div>
                        <p className="text-xs text-slate-500">{org.address || '—'}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{org.institution_type || '—'}</td>
                      <td className="px-4 py-3 text-center">{org.user_count}</td>
                      <td className="px-4 py-3 text-center">{org.job_count}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => setEditTierOrg(org)} className="cursor-pointer" data-testid={`org-tier-${org.org_id}`}>
                          <TierBadge tier={org.subscription_tier} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleFeatured(org.org_id, org.is_featured)} data-testid={`org-feature-${org.org_id}`}>
                          {org.is_featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500 mx-auto" /> : <StarOff className="w-4 h-4 text-slate-300 mx-auto" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`org-actions-${org.org_id}`}><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditTierOrg(org)}>
                              <Edit className="w-4 h-4 mr-2" /> Change Tier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteOrg(org.org_id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {!orgs.length && <tr><td colSpan={7} className="text-center py-8 text-slate-400">No institutions found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />

      {editTierOrg && (
        <EditTierDialog org={editTierOrg} onClose={() => setEditTierOrg(null)} onSave={updateTier} />
      )}
    </div>
  );
};

/* ==================== USERS TAB ==================== */
const UsersTab = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '', role: 'super_admin' });
  const limit = 10;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { skip: page * limit, limit, sort_by: 'created_at', sort_order: 'desc' };
    if (search) params.search = search;
    if (roleFilter !== 'all') params.role = roleFilter;
    try {
      const res = await axios.get(`${API}/admin/users`, { headers, params, withCredentials: true });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, [token, page, search, roleFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const navigate = useNavigate();

  const impersonateUser = async (targetUser) => {
    if (!window.confirm(`Impersonate ${targetUser.name}? You will be logged in as this user.`)) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.post(`${API}/admin/impersonate/${targetUser.user_id}`, {}, { headers, withCredentials: true });
      localStorage.setItem('token', res.data.token);
      toast.success(res.data.message);
      window.location.href = '/dashboard';
    } catch { toast.error('Failed to impersonate'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers, withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updateUserRole = async (userId, newRole) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.put(`${API}/admin/users/${userId}`, { role: newRole }, { headers, withCredentials: true });
      toast.success('Role updated');
      setEditUser(null);
      fetchUsers();
    } catch { toast.error('Failed to update'); }
  };

  const createAdmin = async () => {
    if (!newAdmin.email || !newAdmin.name || !newAdmin.password) {
      toast.error('All fields are required');
      return;
    }
    const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
    try {
      await axios.post(`${API}/admin/create-admin`, newAdmin, { headers, withCredentials: true });
      toast.success(`Admin created: ${newAdmin.email}`);
      setShowCreateAdmin(false);
      setNewAdmin({ email: '', name: '', password: '', role: 'super_admin' });
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create admin'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search users..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="admin-user-search" />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44" data-testid="admin-user-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="client_admin">Client Admin</SelectItem>
            <SelectItem value="team_leader">Team Leader</SelectItem>
            <SelectItem value="recruiter">Recruiter</SelectItem>
            <SelectItem value="candidate">Candidate</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setShowCreateAdmin(true)} className="gap-2" data-testid="create-admin-btn">
          <Plus className="w-4 h-4" /> Create Admin
        </Button>
      </div>

      {/* Create Admin Dialog */}
      <Dialog open={showCreateAdmin} onOpenChange={setShowCreateAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Admin Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <Input value={newAdmin.name} onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})} placeholder="Full Name" className="mt-1" data-testid="new-admin-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})} placeholder="admin@example.com" className="mt-1" data-testid="new-admin-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password</label>
              <Input type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})} placeholder="Minimum 6 characters" className="mt-1" data-testid="new-admin-password" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Role</label>
              <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({...newAdmin, role: v})}>
                <SelectTrigger className="mt-1" data-testid="new-admin-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="client_admin">Client Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAdmin(false)}>Cancel</Button>
            <Button onClick={createAdmin} data-testid="create-admin-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <p className="text-sm text-slate-500">{total} users total</p>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Email</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Role</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Organization</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden xl:table-cell">Source</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden xl:table-cell">Registered</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 truncate max-w-[160px]">{u.name}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]">{u.email}</td>
                      <td className="px-4 py-3 text-center"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-600 truncate max-w-[150px]">{u.organization_name || '—'}</td>
                      <td className="px-4 py-3 hidden xl:table-cell">
                        {u.import_source ? (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">Imported</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Direct</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center hidden md:table-cell">
                        {u.requires_password_reset ? (
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Pwd Reset</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">Active</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden xl:table-cell text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`user-actions-${u.user_id}`}><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => impersonateUser(u)} data-testid={`impersonate-${u.user_id}`}><LogIn className="w-4 h-4 mr-2" /> Impersonate</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditUser(u)}><Edit className="w-4 h-4 mr-2" /> Edit Role</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteUser(u.user_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {!users.length && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No users found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
      {editUser && <EditRoleDialog user={editUser} onClose={() => setEditUser(null)} onSave={updateUserRole} />}
    </div>
  );
};

/* ==================== CANDIDATES TAB ==================== */
const CandidatesTab = ({ token }) => {
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 10;

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { skip: page * limit, limit, sort_by: 'created_at', sort_order: 'desc' };
    if (search) params.search = search;
    try {
      const res = await axios.get(`${API}/admin/candidates`, { headers, params, withCredentials: true });
      setCandidates(res.data.candidates);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load candidates'); }
    setLoading(false);
  }, [token, page, search]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const exportData = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await axios.get(`${API}/admin/export/candidates`, { headers, withCredentials: true });
      downloadCSV(res.data.data, 'candidates_export');
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search candidates..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="admin-candidate-search" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">{total} candidates</span>
          <Button variant="outline" onClick={exportData} data-testid="admin-candidate-export">
            <Download className="w-4 h-4 mr-2" />Export
          </Button>
        </div>
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Candidate</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Contact</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Location</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden xl:table-cell">Skills</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Exp</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Profile</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Apps</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden xl:table-cell">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const p = c.profile || {};
                    const completion = p.profile_completion || 0;
                    return (
                      <tr key={c.user_id} className="border-b last:border-0 hover:bg-indigo-50/50 transition-colors cursor-pointer" data-testid={`candidate-row-${c.user_id}`}
                        onClick={() => window.open(`/candidate/${c.user_id}`, '_blank')}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 truncate max-w-[160px]">{c.name || p.first_name ? `${p.first_name || ''} ${p.last_name || ''}`.trim() || c.email?.split('@')[0] : c.email?.split('@')[0]}</div>
                          {p.current_designation && (
                            <div className="text-xs text-slate-500 truncate max-w-[160px]">{p.current_designation}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-600 truncate max-w-[200px] text-xs">{c.email}</div>
                          {p.phone && <div className="text-slate-500 text-xs mt-0.5">{p.phone}</div>}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="text-slate-600 text-xs truncate max-w-[130px]">
                            {[p.city, p.state].filter(Boolean).join(', ') || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(p.skills || []).slice(0, 2).map((s) => (
                              <Badge key={s} variant="outline" className="text-xs truncate max-w-[90px]">{s}</Badge>
                            ))}
                            {(p.skills?.length || 0) > 2 && (
                              <Badge variant="outline" className="text-xs text-slate-400">+{p.skills.length - 2}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-slate-600">
                          {p.teaching_experience_years ? `${p.teaching_experience_years}y` : '—'}
                        </td>
                        <td className="px-4 py-3 text-center hidden md:table-cell">
                          <div className="flex items-center justify-center gap-1.5">
                            <div className="w-10 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${completion >= 75 ? 'bg-green-500' : completion >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                                style={{ width: `${completion}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-500">{completion}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline" className={`text-xs ${c.application_count > 0 ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : ''}`}>
                            {c.application_count}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell text-slate-500 text-xs">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {!candidates.length && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No candidates found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

/* ==================== JOBS TAB ==================== */
const JobsTab = ({ token }) => {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 10;

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { skip: page * limit, limit, sort_by: 'created_at', sort_order: 'desc' };
    if (search) params.search = search;
    if (statusFilter !== 'all') params.status = statusFilter;
    try {
      const res = await axios.get(`${API}/admin/jobs`, { headers, params, withCredentials: true });
      setJobs(res.data.jobs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load jobs'); }
    setLoading(false);
  }, [token, page, search, statusFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const toggleFeatured = async (jobId, current) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/feature?is_featured=${!current}`, {}, { headers, withCredentials: true });
      toast.success(current ? 'Unfeatured' : 'Featured');
      fetchJobs();
    } catch { toast.error('Failed to update'); }
  };

  const toggleSponsor = async (jobId, current) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/sponsor?is_sponsored=${!current}`, {}, { headers, withCredentials: true });
      toast.success(current ? 'Unsponsored' : 'Sponsored');
      fetchJobs();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search jobs..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="admin-job-search" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
          <SelectTrigger className="w-40" data-testid="admin-job-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Job Title</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Institution</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Posted</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Apps</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Featured</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Sponsored</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.job_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{job.title}</p>
                        <p className="text-xs text-slate-500">{job.location}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{job.organization_name || '—'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">
                        {(job.published_at || job.created_at) ? new Date(job.published_at || job.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={job.status} /></td>
                      <td className="px-4 py-3 text-center">{job.application_count}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleFeatured(job.job_id, job.is_featured)} data-testid={`job-feature-${job.job_id}`}>
                          {job.is_featured ? <Star className="w-4 h-4 text-amber-500 fill-amber-500 mx-auto" /> : <StarOff className="w-4 h-4 text-slate-300 mx-auto" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleSponsor(job.job_id, job.is_sponsored)} data-testid={`job-sponsor-${job.job_id}`}>
                          {job.is_sponsored ? <Megaphone className="w-4 h-4 text-indigo-600 fill-indigo-100 mx-auto" /> : <Megaphone className="w-4 h-4 text-slate-300 mx-auto" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <a href={`/jobs/${job.job_id}`} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                  {!jobs.length && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No jobs found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

/* ==================== BLOGS TAB ==================== */
const BlogsTab = ({ token }) => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { limit: 20 };
    if (search) params.search = search;
    if (statusFilter !== 'all') params.status = statusFilter;
    try {
      const res = await axios.get(`${API}/admin/blogs`, { headers, params, withCredentials: true });
      setBlogs(res.data.blogs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load blogs'); }
    setLoading(false);
  }, [token, search, statusFilter]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const deleteBlog = async (blogId) => {
    if (!window.confirm('Delete this blog post?')) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.delete(`${API}/blogs/${blogId}`, { headers, withCredentials: true });
      toast.success('Blog deleted');
      fetchBlogs();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (blog) => {
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const newStatus = blog.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/blogs/${blog.blog_id}`, { status: newStatus }, { headers, withCredentials: true });
      toast.success(newStatus === 'published' ? 'Published' : 'Unpublished');
      fetchBlogs();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-blogs-tab">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search blogs..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-blog-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => navigate('/blogs/new')} data-testid="admin-create-blog-btn">
          <Plus className="w-4 h-4 mr-2" />New Post
        </Button>
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Title</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Category</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Author</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Date</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blogs.map((b) => (
                    <tr key={b.blog_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-900 max-w-xs truncate">{b.title}</td>
                      <td className="px-4 py-3 text-center"><Badge variant="outline" className="text-xs">{b.category}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={b.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>{b.status}</Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{b.author_name}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">{new Date(b.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/blogs/${b.blog_id}/edit`)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => togglePublish(b)}>
                              <Eye className="w-4 h-4 mr-2" /> {b.status === 'published' ? 'Unpublish' : 'Publish'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => deleteBlog(b.blog_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                  {!blogs.length && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No blog posts found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <p className="text-sm text-slate-400">{total} post{total !== 1 ? 's' : ''} total</p>
    </div>
  );
};

/* ==================== JOB ALERTS TAB ==================== */
const JobAlertsTab = ({ token }) => {
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { limit: 20 };
    if (search) params.search = search;
    try {
      const res = await axios.get(`${API}/admin/job-alerts`, { headers, params, withCredentials: true });
      setAlerts(res.data.alerts);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load job alerts'); }
    setLoading(false);
  }, [token, search]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const deleteAlert = async (alertId) => {
    if (!window.confirm('Delete this alert?')) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.delete(`${API}/admin/job-alerts/${alertId}`, { headers, withCredentials: true });
      toast.success('Alert deleted');
      fetchAlerts();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-alerts-tab">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search alerts..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-alert-search" />
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">User</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Keywords</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Location</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Frequency</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Active</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.alert_id} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{a.user_name}</p>
                        <p className="text-xs text-slate-500">{a.user_email}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{a.keywords || '—'}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{a.location || '—'}</td>
                      <td className="px-4 py-3 text-center"><Badge variant="outline" className="text-xs">{a.frequency || 'daily'}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={a.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                          {a.is_active !== false ? 'Active' : 'Paused'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => deleteAlert(a.alert_id)} data-testid={`delete-alert-${a.alert_id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!alerts.length && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No job alerts found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <p className="text-sm text-slate-400">{total} alert{total !== 1 ? 's' : ''} total</p>
    </div>
  );
};

/* ==================== TRANSACTIONS TAB ==================== */
const TransactionsTab = ({ token }) => {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 15;

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { limit, skip: page * limit };
    if (search) params.search = search;
    if (tierFilter) params.tier = tierFilter;
    if (statusFilter) params.status = statusFilter;
    try {
      const res = await axios.get(`${API}/admin/transactions`, { headers, params, withCredentials: true });
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
    } catch { toast.error('Failed to load transactions'); }
    setLoading(false);
  }, [token, search, tierFilter, statusFilter, page]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-transactions-tab">
      {/* Revenue Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4 border-l-4 border-l-green-500">
          <p className="text-xs text-slate-500 font-medium uppercase">Total Revenue</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="txn-total-revenue">₹{(stats.total_revenue || 0).toLocaleString()}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-blue-500">
          <p className="text-xs text-slate-500 font-medium uppercase">This Month</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="txn-monthly-revenue">₹{(stats.monthly_revenue || 0).toLocaleString()}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-indigo-500">
          <p className="text-xs text-slate-500 font-medium uppercase">Active Subscriptions</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="txn-active-subs">{stats.active_subscriptions || 0}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500">
          <p className="text-xs text-slate-500 font-medium uppercase">By Tier</p>
          <div className="flex gap-2 mt-1 flex-wrap">
            {Object.entries(stats.by_tier || {}).map(([t, c]) => (
              <Badge key={t} className="text-xs bg-slate-100 text-slate-600">{t}: {c}</Badge>
            ))}
            {!stats.by_tier || Object.keys(stats.by_tier).length === 0 ? <span className="text-sm text-slate-400">—</span> : null}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by institution..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9" data-testid="admin-txn-search" />
        </div>
        <select value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(0); }} className="border rounded-md px-3 py-2 text-sm bg-white" data-testid="txn-tier-filter">
          <option value="">All Tiers</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="border rounded-md px-3 py-2 text-sm bg-white" data-testid="txn-status-filter">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Transaction ID</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Institution</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Type</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Tier</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Amount</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Payment</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t, idx) => (
                    <tr key={t.txn_id || idx} className="border-b last:border-0 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{t.txn_id}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{t.org_name || '—'}</td>
                      <td className="px-4 py-3 text-center"><Badge variant="outline" className="text-xs">{t.type}</Badge></td>
                      <td className="px-4 py-3 text-center"><TierBadge tier={t.tier} /></td>
                      <td className="px-4 py-3 text-right font-medium">
                        {t.amount > 0 ? `₹${t.amount.toLocaleString()}` : <span className="text-slate-400">Free</span>}
                      </td>
                      <td className="px-4 py-3 text-center text-xs text-slate-500">{t.payment_method || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={
                          t.status === 'paid' || t.status === 'completed' ? 'bg-green-100 text-green-700' :
                          t.status === 'active' ? 'bg-blue-100 text-blue-700' :
                          t.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          t.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }>{t.status}</Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs">{new Date(t.date || t.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {!transactions.length && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No transactions found</td></tr>}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-slate-500">Page {page + 1} of {totalPages} ({total} total)</p>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* ==================== IMPORTS TAB ==================== */
const ImportsTab = ({ token }) => {
  const [imports, setImports] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchImports = useCallback(async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { limit: 20 };
    if (search) params.search = search;
    try {
      const res = await axios.get(`${API}/admin/imports`, { headers, params, withCredentials: true });
      setImports(res.data.imports);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load imports'); }
    setLoading(false);
  }, [token, search]);

  useEffect(() => { fetchImports(); }, [fetchImports]);

  return (
    <div className="space-y-4" data-testid="admin-imports-tab">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search imports..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="admin-import-search" />
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">File</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Entity</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Total</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Success</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Failed</th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">By</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {imports.map((imp) => (
                    <tr key={imp.import_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{imp.file_name || '—'}</td>
                      <td className="px-4 py-3 text-center"><Badge variant="outline" className="text-xs">{imp.entity}</Badge></td>
                      <td className="px-4 py-3 text-center">{imp.total_records}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-medium">{imp.successful}</td>
                      <td className="px-4 py-3 text-center text-red-500">{imp.failed}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={imp.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{imp.status}</Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-slate-600">{imp.imported_by_name}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-slate-500 text-xs">{new Date(imp.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {!imports.length && <tr><td colSpan={8} className="text-center py-8 text-slate-400">No imports found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <p className="text-sm text-slate-400">{total} import{total !== 1 ? 's' : ''} total</p>
    </div>
  );
};

/* ==================== AUDIT LOGS TAB ==================== */
const ACTION_LABELS = {
  impersonate_user: { label: 'Impersonated User', color: 'bg-amber-100 text-amber-700' },
  delete_user: { label: 'Deleted User', color: 'bg-red-100 text-red-700' },
  delete_organization: { label: 'Deleted Organization', color: 'bg-red-100 text-red-700' },
  change_tier: { label: 'Changed Tier', color: 'bg-blue-100 text-blue-700' },
  change_role: { label: 'Changed Role', color: 'bg-purple-100 text-purple-700' },
  create_tenant: { label: 'Created Tenant', color: 'bg-green-100 text-green-700' },
  update_tenant: { label: 'Updated Tenant', color: 'bg-sky-100 text-sky-700' },
  delete_tenant: { label: 'Deleted Tenant', color: 'bg-red-100 text-red-700' },
  update_cms: { label: 'Updated CMS', color: 'bg-indigo-100 text-indigo-700' },
};

const AuditLogsTab = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [stats, setStats] = useState(null);
  const limit = 25;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams({ skip: (page - 1) * limit, limit });
      if (search) params.append('search', search);
      if (actionFilter) params.append('action', actionFilter);
      const res = await axios.get(`${API}/admin/audit-logs?${params}`, { headers, withCredentials: true });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load audit logs'); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/audit-logs/stats`, { headers, withCredentials: true });
      setStats(res.data);
    } catch {}
  };

  useEffect(() => { fetchLogs(); }, [page, actionFilter]);
  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { const t = setTimeout(() => { setPage(1); fetchLogs(); }, 400); return () => clearTimeout(t); }, [search]);

  const handleExportXlsx = () => {
    window.open(`${API}/admin/audit-logs/export?format=xlsx`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="audit-logs-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-xs text-slate-500">Total Events</p>
          </CardContent></Card>
          {Object.entries(stats.by_action || {}).slice(0, 3).map(([action, count]) => (
            <Card key={action}><CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{count}</p>
              <p className="text-xs text-slate-500 capitalize">{action.replace(/_/g, ' ')}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search logs..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9" data-testid="audit-search-input" />
            </div>
            <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(1); }}
              className="border rounded-md px-3 py-2 text-sm" data-testid="audit-action-filter">
              <option value="">All Actions</option>
              {Object.entries(ACTION_LABELS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <Button variant="outline" size="sm" onClick={handleExportXlsx} data-testid="audit-export-btn">
              <Download className="w-4 h-4 mr-2" /> Export XLSX
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Log Entries */}
      <Card>
        <CardContent className="p-0">
          {loading ? <LoadingState /> : logs.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ShieldAlert className="w-10 h-10 mx-auto mb-2" />
              <p>No audit events recorded yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {logs.map(log => {
                const meta = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-slate-100 text-slate-700' };
                return (
                  <div key={log.audit_id} className="px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`audit-row-${log.audit_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Clock className="w-4 h-4 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                          <span className="text-sm font-medium text-slate-800">{log.actor_name || 'System'}</span>
                          <span className="text-xs text-slate-400">({log.actor_role})</span>
                        </div>
                        <div className="mt-1 text-sm text-slate-600">
                          {log.action === 'impersonate_user' && `Impersonated ${log.details?.target_email} (${log.details?.target_role})`}
                          {log.action === 'delete_user' && `Deleted user ${log.details?.deleted_email} (${log.details?.deleted_role})`}
                          {log.action === 'delete_organization' && `Deleted org "${log.details?.org_name}"`}
                          {log.action === 'change_tier' && `Changed "${log.details?.org_name}" from ${log.details?.old_tier} → ${log.details?.new_tier}`}
                          {log.action === 'change_role' && `Changed ${log.details?.email} from ${log.details?.old_role} → ${log.details?.new_role}`}
                          {log.action === 'create_tenant' && `Created tenant "${log.details?.name}" (${log.details?.tier})`}
                          {log.action === 'update_tenant' && `Updated tenant "${log.details?.name}"`}
                          {log.action === 'delete_tenant' && `Deleted tenant "${log.details?.name}"`}
                          {log.action === 'update_cms' && `Updated CMS fields: ${log.details?.fields_updated?.join(', ')}`}
                          {!ACTION_LABELS[log.action] && `Resource: ${log.resource_type} — ${log.resource_id}`}
                        </div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <p className="text-xs text-slate-400">{new Date(log.timestamp).toLocaleString()}</p>
                        {log.ip_address && <p className="text-xs text-slate-300">IP: {log.ip_address}</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      <Pagination page={page} setPage={setPage} total={total} limit={limit} />
    </div>
  );
};

/* ==================== SHARED COMPONENTS ==================== */
const RoleBadge = ({ role }) => {
  const map = { super_admin: 'bg-red-100 text-red-700', client_admin: 'bg-purple-100 text-purple-700', team_leader: 'bg-blue-100 text-blue-700', recruiter: 'bg-green-100 text-green-700', candidate: 'bg-slate-100 text-slate-700' };
  const labels = { super_admin: 'Super Admin', client_admin: 'Client Admin', team_leader: 'Team Lead', recruiter: 'Recruiter', candidate: 'Candidate' };
  return <Badge className={map[role] || map.candidate}>{labels[role] || role}</Badge>;
};

const TierBadge = ({ tier }) => {
  const map = {
    basic: 'bg-slate-100 text-slate-700 border-slate-200',
    pro: 'bg-blue-100 text-blue-700 border-blue-200',
    enterprise: 'bg-purple-100 text-purple-700 border-purple-200',
    unlimited: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    partner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    monthly: 'bg-blue-100 text-blue-700 border-blue-200',
    yearly: 'bg-blue-100 text-blue-700 border-blue-200',
    pack: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    free: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return <Badge className={`border ${map[tier] || map.basic}`}>{tier || 'basic'}</Badge>;
};

export const PartnerBadge = ({ org, className = '' }) => {
  const isPartner = org?.is_partner || org?.is_unrestricted ||
    ['pro', 'monthly', 'yearly', 'enterprise', 'unlimited', 'partner'].includes(org?.subscription_tier);
  if (!isPartner) return null;
  return (
    <Badge
      className={`bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] px-1.5 py-0 h-4 gap-1 font-medium ${className}`}
      data-testid="partner-badge"
    >
      <Sparkles className="w-2.5 h-2.5" /> Partner
    </Badge>
  );
};

const StatusBadge = ({ status }) => {
  const map = { draft: 'bg-slate-100 text-slate-700', published: 'bg-green-100 text-green-700', closed: 'bg-red-100 text-red-700' };
  return <Badge className={map[status] || map.draft}>{status}</Badge>;
};

const Pagination = ({ page, setPage, total, limit }) => {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between" data-testid="admin-pagination">
      <p className="text-sm text-slate-500">Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}><ChevronLeft className="w-4 h-4" /></Button>
        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

const LoadingState = () => (
  <div className="flex justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
  </div>
);

const EditRoleDialog = ({ user, onClose, onSave }) => {
  const [role, setRole] = useState(user.role);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-role-dialog">
        <DialogHeader><DialogTitle>Edit Role — {user.name}</DialogTitle></DialogHeader>
        <div className="py-4">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="client_admin">Client Admin</SelectItem>
              <SelectItem value="team_leader">Team Leader</SelectItem>
              <SelectItem value="recruiter">Recruiter</SelectItem>
              <SelectItem value="candidate">Candidate</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(user.user_id, role)} data-testid="edit-role-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditTierDialog = ({ org, onClose, onSave }) => {
  const [tier, setTier] = useState(org.subscription_tier || 'basic');
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-tier-dialog">
        <DialogHeader><DialogTitle>Set Tier — {org.name}</DialogTitle></DialogHeader>
        <div className="py-4 space-y-3">
          <p className="text-sm text-slate-500">Current tier: <span className="font-medium">{org.subscription_tier || 'basic'}</span></p>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger data-testid="edit-tier-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="basic">Basic (Free)</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(org.org_id, tier)} data-testid="edit-tier-save">Update Tier</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const downloadCSV = (data, filename) => {
  if (!data?.length) { toast.error('No data to export'); return; }
  const flattenObj = (obj, prefix = '') => {
    const result = {};
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      const newKey = prefix ? `${prefix}_${key}` : key;
      if (val && typeof val === 'object' && !Array.isArray(val)) {
        Object.assign(result, flattenObj(val, newKey));
      } else if (Array.isArray(val)) {
        result[newKey] = val.join('; ');
      } else {
        result[newKey] = val;
      }
    }
    return result;
  };
  const flat = data.map(d => flattenObj(d));
  const headers = [...new Set(flat.flatMap(Object.keys))];
  const csvRows = [headers.join(',')];
  for (const row of flat) {
    csvRows.push(headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(','));
  }
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export default AdminPage;
